import { ActivatedRouteSnapshot, Router, Routes } from '@angular/router';
import { Entities, Permissions, PermissionsService } from './services/permissions.service';
import { Injector, inject } from '@angular/core';
import { LoggingService } from './services/logging.service';
import { WelcomeComponent } from './pages/welcome/welcome.component';
import { Observable, from, map, of, switchMap } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { NzModalService } from 'ng-zorro-antd/modal';
import { AmplifyService } from './services/amplify.service';

export const APP_ROUTES: Routes = [
  { path: '', pathMatch: 'full', redirectTo: '/welcome' },
  {
    path: 'welcome',
    component: WelcomeComponent,
    canActivate: [(route: ActivatedRouteSnapshot): Observable<boolean> => canActivate(route, Entities.Welcome)],
  },
  {
    path: 'domain-values',
    loadChildren: () => import('./pages/domain-values/domain-values.routes').then((r) => r.DOMAIN_VALUES_ROUTES),
  },
  {
    path: 'organizations',
    loadChildren: () => import('./pages/organizations/organizations.routes').then((r) => r.ORGANIZATIONS_ROUTES),
  },
  {
    path: 'investment-projects',
    loadChildren: () =>
      import('./pages/investment-projects/investment-projects.routes').then((r) => r.INVESTMENT_PROJECTS_ROUTES),
  },
  {
    path: 'permissions',
    loadChildren: () => import('./pages/permissions/permissions.routes').then((r) => r.PERMISSIONS_ROUTES),
  },
  {
    path: 'users',
    loadChildren: () => import('./pages/users/users.routes').then((r) => r.USERS_ROUTES),
  },
  { path: '**', redirectTo: '/welcome', pathMatch: 'full' },
];

export function canActivate(route: ActivatedRouteSnapshot, entity: string): Observable<boolean> {
  const permissionsService = inject(PermissionsService);
  const logger = inject(LoggingService);
  const router = inject(Router);
  const injector = inject(Injector);
  const amplifyService = inject(AmplifyService);

  if (!amplifyService.isSignedIn()) {
    return of(true);
  }

  return from(permissionsService.loadPermissions()).pipe(
    switchMap(() => toObservable(permissionsService.can(Permissions.Read, entity), { injector: injector })),
    map((userCan) => {
      if (!userCan) {
        logger.error(new Error('Not authorized to view this page: ' + route.url), false);
        router.navigate(['/welcome']);
      }

      return userCan;
    }),
  );
}

export interface CanComponentDeactivate {
  canDeactivate: () => boolean | Observable<boolean>;
}

export function canDeactivate(component: CanComponentDeactivate): Observable<boolean> {
  const modal = inject(NzModalService);

  if (component?.canDeactivate()) {
    return of(true);
  }

  const title = $localize`WARNING!`;
  const message = $localize`You have unsaved changes. Press "Continue editing" to stay on this page or "Quit the page" to exit the page and lose your changes.`;

  const confirmModal = modal.confirm({
    nzTitle: title,
    nzContent: message,
    nzOkText: $localize`Quit the page`,
    nzCancelText: $localize`Continue editing`,
    nzOnOk: () => true,
  });

  return confirmModal.afterClose.pipe(
    map((result) => {
      return result === true;
    }),
  );
}
